/**
 * Thesis Fit — Type Definitions
 *
 * Types for the investor thesis weighting system.
 * Dimension-agnostic: reads from DIMENSION_ORDER so adding
 * new dimensions requires no code changes here.
 */

import type { ScoringDimension } from '@/lib/scoring/methodology';

// =============================================================================
// Archetype & Label Types
// =============================================================================

export type ThesisArchetype =
  | 'balanced'
  | 'traction'
  | 'tech-team'
  | 'defensibility'
  | 'custom';

export type FitLabel =
  | 'strong-fit'
  | 'good-fit'
  | 'neutral'
  | 'weak-fit'
  | 'mismatch';

// =============================================================================
// Thesis Profile
// =============================================================================

export interface ThesisProfile {
  id: string;
  investorId: string;
  name: string;
  archetype: ThesisArchetype;
  /** Weight per dimension. Values in [0.02, 0.20], sum = 1.0 */
  weights: Record<ScoringDimension, number>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Thesis Score (computed client-side)
// =============================================================================

export interface ThesisDimensionDetail {
  dimension: ScoringDimension;
  label: string;
  score: number;
  thesisWeight: number;
  neutralWeight: number;
  weightedContribution: number;
  /** Positive = investor over-weights vs neutral */
  weightDelta: number;
}

export interface ThesisScore {
  thesisScore: number;
  inverioScore: number;
  fitGap: number;
  fitLabel: FitLabel;
  fitColor: string;
  dimensionDetails: ThesisDimensionDetail[];
  summary: string;
}

// =============================================================================
// Constraints
// =============================================================================

export const WEIGHT_CONSTRAINTS = {
  min: 0.02,
  max: 0.20,
  sumTarget: 1.0,
  tolerance: 0.001,
} as const;

// =============================================================================
// Fit Thresholds & Colors
// =============================================================================

export const FIT_THRESHOLDS: { min: number; label: FitLabel }[] = [
  { min: 15, label: 'strong-fit' },
  { min: 5, label: 'good-fit' },
  { min: -4, label: 'neutral' },
  { min: -14, label: 'weak-fit' },
  { min: -Infinity, label: 'mismatch' },
];

export const FIT_COLORS: Record<FitLabel, string> = {
  'strong-fit': '#16a34a',  // green-600
  'good-fit': '#22c55e',    // green-500
  neutral: '#a3a3a3',       // neutral-400
  'weak-fit': '#f97316',    // orange-500
  mismatch: '#ef4444',      // red-500
};

export const FIT_LABELS_DISPLAY: Record<FitLabel, string> = {
  'strong-fit': 'Strong Fit',
  'good-fit': 'Good Fit',
  neutral: 'Neutral',
  'weak-fit': 'Weak Fit',
  mismatch: 'Mismatch',
};

// =============================================================================
// Archetype Metadata (for UI display)
// =============================================================================

export interface ArchetypeInfo {
  id: ThesisArchetype;
  name: string;
  description: string;
  emoji: string;
}

export const ARCHETYPE_INFO: ArchetypeInfo[] = [
  {
    id: 'balanced',
    name: 'Balanced Generalist',
    description: 'Equal weight across all dimensions',
    emoji: '=',
  },
  {
    id: 'traction',
    name: 'Traction-Driven',
    description: 'Revenue, growth, and go-to-market focus',
    emoji: '^',
  },
  {
    id: 'tech-team',
    name: 'Tech & Team First',
    description: 'Team quality, product depth, and velocity',
    emoji: '*',
  },
  {
    id: 'defensibility',
    name: 'Defensibility-Focused',
    description: 'Moats, competitive position, and timing',
    emoji: '#',
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Start from balanced, adjust freely',
    emoji: '~',
  },
];
